/**
 * OperatingHoursMenuSection — Hours display in visitor menu.
 *
 * Shows today's hours by default, with a collapsible weekly schedule.
 * Shown when operatingConfig has defaultOpen/defaultClose, regardless of mode.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { computeMapOperatingStatus, type OperatingConfig, type OperatingMode } from "@wondermaps/maps";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  cn,
} from "@wondermaps/ui";

const DAY_SHORT_KEYS = [
  "operating.days.shortSun",
  "operating.days.shortMon",
  "operating.days.shortTue",
  "operating.days.shortWed",
  "operating.days.shortThu",
  "operating.days.shortFri",
  "operating.days.shortSat",
];

interface OperatingHoursMenuSectionProps {
  operatingMode: OperatingMode;
  operatingConfig: OperatingConfig | null;
  timezone: string;
}

/** Format "HH:MM" → locale-aware time (e.g. "9:00 AM" en, "9:00" fr) */
function formatTime(t24: string, locale: string): string {
  const [h, m] = t24.split(":").map(Number);
  const date = new Date(2000, 0, 1, h, m);
  return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

/** Format "MM-DD" → locale-aware short date (e.g. "May 1" en, "1 mai" fr) */
function formatMMDD(mmdd: string, locale: string): string {
  const [m, d] = mmdd.split("-").map(Number);
  const date = new Date(2000, m - 1, d);
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function OperatingHoursMenuSection({
  operatingMode,
  operatingConfig,
  timezone,
}: OperatingHoursMenuSectionProps) {
  const { t, i18n } = useTranslation("map");
  const locale = i18n.language;
  const [weekOpen, setWeekOpen] = useState(false);

  const status = useMemo(
    () => computeMapOperatingStatus(operatingMode, operatingConfig, timezone),
    [operatingMode, operatingConfig, timezone]
  );

  if (!operatingConfig?.defaultOpen || !operatingConfig?.defaultClose) return null;

  // Season info for seasonal mode (rendered as muted footnote, not a substitute)
  const seasonLine = operatingMode === "seasonal" && operatingConfig.seasonStart && operatingConfig.seasonEnd
    ? t("operating.season", { start: formatMMDD(operatingConfig.seasonStart, locale), end: formatMMDD(operatingConfig.seasonEnd, locale) })
    : null;

  const isClosedForSeason = status.state === "closed_season";
  const todayDow = new Date().getDay();

  // Today's hours
  const todayClosure = useMemo(() => {
    if (!operatingConfig.closureDates?.length) return null;
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const mmdd = `${mm}-${dd}`;
    const yyyymmdd = `${now.getFullYear()}-${mm}-${dd}`;
    for (const c of operatingConfig.closureDates) {
      const match = c.recurring ? c.date === mmdd : c.date === yyyymmdd;
      if (match) return c.label ?? t("operating.closedDay");
    }
    return null;
  }, [operatingConfig.closureDates, t]);

  const todayIsNonOperating = !todayClosure &&
    operatingConfig.operatingDays &&
    operatingConfig.operatingDays.length > 0 &&
    operatingConfig.operatingDays.length < 7 &&
    !operatingConfig.operatingDays.includes(todayDow);

  const todayOverride = operatingConfig.dayOverrides?.find((d) => d.day === todayDow);
  const todayOpen = todayOverride?.open ?? operatingConfig.defaultOpen;
  const todayClose = todayOverride?.close ?? operatingConfig.defaultClose;

  // Pre-compute closure dates for the next 7 days (for weekly display)
  const closedDates = useMemo(() => {
    if (!operatingConfig.closureDates || operatingConfig.closureDates.length === 0) return new Map<number, string>();
    const map = new Map<number, string>();
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (let offset = 0; offset < 7; offset++) {
      const d = new Date(base.getTime() + offset * 86_400_000);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const mmdd = `${mm}-${dd}`;
      const yyyymmdd = `${d.getFullYear()}-${mm}-${dd}`;
      for (const c of operatingConfig.closureDates) {
        const match = c.recurring ? c.date === mmdd : c.date === yyyymmdd;
        if (match) {
          map.set(offset, c.label ?? t("operating.closedDay"));
          break;
        }
      }
    }
    return map;
  }, [operatingConfig.closureDates, t]);

  // Status label + color
  const statusLabel = isClosedForSeason
    ? t("operating.closedForSeason")
    : todayClosure
      ? todayClosure
      : todayIsNonOperating
        ? t("operating.closedDay")
        : status.isOpen
          ? t("operating.openNow")
          : t("operating.closed");
  const statusColor = status.isOpen
    ? "text-emerald-600"
    : isClosedForSeason || todayClosure || todayIsNonOperating || !status.isOpen
      ? "text-destructive"
      : "text-muted-foreground";
  const todayHoursLabel = !isClosedForSeason && !todayClosure && !todayIsNonOperating
    ? `${formatTime(todayOpen, locale)}–${formatTime(todayClose, locale)}`
    : null;

  const canExpand = !isClosedForSeason;

  return (
    <div className="px-4 py-3 border-b">
      <Collapsible open={weekOpen} onOpenChange={setWeekOpen}>
        <CollapsibleTrigger asChild disabled={!canExpand}>
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-between gap-3 text-sm min-h-[44px]",
              canExpand && "cursor-pointer",
            )}
            aria-label={t("operating.toggleWeek")}
          >
            <span className={cn("flex items-center gap-2 font-medium", statusColor)}>
              <FontAwesomeIcon icon={faClock} className="h-4 w-4" />
              {statusLabel}
            </span>
            <span className="flex items-center gap-2 text-muted-foreground">
              {todayHoursLabel && <span>{todayHoursLabel}</span>}
              {canExpand && (
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={cn("h-3 w-3 transition-transform", weekOpen && "rotate-180")}
                />
              )}
            </span>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-0.5 mt-2 pt-2 border-t border-border/50">
            {Array.from({ length: 6 }, (_, i) => {
              const offset = i + 1; // skip today (already shown in trigger)
              const dow = (todayDow + offset) % 7;
              const closureLabel = closedDates.get(offset);
              const isNonOperatingDay = !closureLabel &&
                operatingConfig.operatingDays &&
                operatingConfig.operatingDays.length > 0 &&
                operatingConfig.operatingDays.length < 7 &&
                !operatingConfig.operatingDays.includes(dow);

              const dayOverride = operatingConfig.dayOverrides?.find((d) => d.day === dow);
              const open = dayOverride?.open ?? operatingConfig.defaultOpen!;
              const close = dayOverride?.close ?? operatingConfig.defaultClose!;

              return (
                <div key={dow} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {t(DAY_SHORT_KEYS[dow])}
                  </span>
                  {closureLabel ? (
                    <span className="text-destructive font-medium">{closureLabel}</span>
                  ) : isNonOperatingDay ? (
                    <span className="text-muted-foreground">{t("operating.closedDay")}</span>
                  ) : (
                    <span>{formatTime(open, locale)}–{formatTime(close, locale)}</span>
                  )}
                </div>
              );
            })}
            {seasonLine && (
              <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border/50">{seasonLine}</p>
            )}
            {isClosedForSeason && status.seasonStart && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("operating.opensDate", { date: status.seasonStart })}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
